// Route: /auth/login — БОЛЬШЕ НЕ СТРАНИЦА.
//
// Решение владельца (2026-08-16): отдельной страницы входа в сервисе нет.
// Единственная поверхность входа — глобальная модалка (`LoginModal`), поэтому
// адрес остаётся только как совместимость: он открывает модалку и уводит на
// главную. Так же гасится и старый побочный эффект «вышел из аккаунта — попал
// на чужой экран входа с устаревшим лендинг-блоком»: любой путь, который вёл
// сюда (revoked-сессия, истёкшая сессия, SSO-ошибка, отказ от согласия),
// теперь приземляется на главную.
//
// `?admin=true` — единственное исключение: скрытая форма email/пароль для
// тестового входа админом (rules/auth.md). Без неё вход по паролю недоступен
// нигде, а он нужен для проверок на проде.
//
// Search params: ?returnTo=/route, ?error=<code>, ?admin=true, ?demo=…
// См. docs/ui-spec/auth.login.{desktop,mobile}.md
import { tolerantSearch } from "@studyhub/shared/utils/tolerant-search";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { LoginForm } from "@/components/auth/login-form";
import { PageMeta } from "@/components/seo/PageMeta";
import { PagePendingFallback } from "@/components/shared/page-pending-fallback";
import { useLoginModalStore } from "@/stores/login-modal";

const searchSchema = z.object({
	returnTo: z.string().optional(),
	plan: z.string().optional(),
	demo: z.enum(["default", "loading", "error", "banned"]).optional(),
	error: z.string().optional(),
	// ?admin=true|1 → скрытая форма email/пароль (вход админом для тестов, rules/auth.md).
	// transform → undefined (а не false), чтобы ключ НЕ сериализовался в URL и не
	// раскрывал существование admin-режима при обычном переходе на форму входа.
	admin: z
		.union([z.boolean(), z.literal("1"), z.literal("true")])
		.optional()
		.transform((v) => (v === true || v === "1" || v === "true" ? true : undefined)),
});

export const Route = createFileRoute("/auth/login")({
	validateSearch: tolerantSearch(searchSchema),
	beforeLoad: ({ search }) => {
		// Демо-режимы (?demo=…) нужны для скриншотов ui-spec — их тоже оставляем.
		if (search.admin === true || (search.demo && search.demo !== "default")) return;
		useLoginModalStore.getState().open(search.returnTo, search.error);
		throw redirect({ to: "/", replace: true });
	},
	component: LoginPage,
	pendingComponent: PagePendingFallback,
});

function LoginPage(): React.ReactElement {
	const search = Route.useSearch();
	const { t } = useTranslation("auth");
	return (
		<>
			<PageMeta
				title={t("login.title")}
				description={t("login.heroLead")}
				path="/auth/login"
				robots="noindex, follow"
			/>
			<LoginForm
				demoMode={search.demo ?? "default"}
				initialError={search.error}
				adminMode={search.admin === true}
				returnTo={search.returnTo}
			/>
		</>
	);
}
